import React, { useMemo } from 'react';
import { PogProductContent } from '../pages/store-details/components/PogProductContent';
import { ProductRecognitionDetectionContent } from '../pages/store-details/components/ProductRecognitionDetectionContent';
import {
  CameraImageResponse,
  InferenceMetadata,
  PlanogramComplianceResponse,
  ProductRecognitionBBox,
  ProductRecognitionMask,
  ProductRecognitionPolygon,
} from '../types';
import { getConfidenceClass, mapOcrConfidenceToLabel } from '../utils/confidenceUtils';

const humanizeMetadataKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatMetadataValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

interface ImageItemDetailsDialogProps {
  item: ProductRecognitionBBox | ProductRecognitionPolygon | ProductRecognitionMask | any;
  cameraImageElement?: HTMLImageElement;
  isOpen: boolean;
  onClose: () => void;
  layerBboxColor?: string;
  chainId?: number;
  storeId?: number;
  storeName?: string;
  onUpcSubmit?: (upc: string) => void;
  shouldShowEmbeddings?: boolean;
  planogramComplianceData?: PlanogramComplianceResponse | null;
  cameraImage?: CameraImageResponse | null;
  cartonBoxes?: ProductRecognitionBBox[] | null;
  inferenceMetadata?: InferenceMetadata | null;
}

export const ImageItemDetailsDialog: React.FC<ImageItemDetailsDialogProps> = ({
  item,
  cameraImageElement,
  isOpen,
  onClose,
  layerBboxColor,
  chainId,
  storeId,
  storeName,
  onUpcSubmit,
  shouldShowEmbeddings = false,
  planogramComplianceData,
  cameraImage,
  cartonBoxes,
  inferenceMetadata,
}) => {
  const inferenceMetadataFiltered = useMemo(() => {
    if (!inferenceMetadata) {
      return inferenceMetadata;
    }

    const { oos_model_version, ...rest } = inferenceMetadata;
    return rest;
  }, [inferenceMetadata]);

  if (!isOpen) {
    return null;
  }

  const handleUpcSubmit = (upc: string) => {
    const trimmedUpc = upc.trim();

    if (!trimmedUpc) {
      return;
    }

    onUpcSubmit?.(trimmedUpc);
  };

  const isProductRecognitionItem = (
    item: any
  ): item is ProductRecognitionBBox | ProductRecognitionPolygon => {
    return (
      item &&
      typeof item === 'object' &&
      (('x1' in item && 'y1' in item && 'x2' in item && 'y2' in item) || 'points' in item) &&
      !isPogItem(item) && // Exclude POG items from product recognition items
      !isTemplateItem(item) // Exclude template items from product recognition items
    );
  };

  const isPogItem = (item: any): boolean => {
    return (
      item &&
      typeof item === 'object' &&
      'x1' in item &&
      'y1' in item &&
      'x2' in item &&
      'y2' in item &&
      'upc' in item &&
      'name' in item &&
      'ilItemId' in item &&
      !('shape' in item) // POG items don't have the shape array that product recognition items have
    );
  };

  const isTemplateItem = (item: any): boolean => {
    return (
      item &&
      typeof item === 'object' &&
      'bboxId' in item &&
      'x1' in item &&
      'y1' in item &&
      'x2' in item &&
      'y2' in item &&
      'upc' in item &&
      'tagName' in item &&
      'isActive' in item &&
      'productId' in item
    );
  };

  const renderDetailField = (label: string, value: any) => {
    if (value === undefined || value === null) return null;

    return (
      <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
        <span className="text-gray-600 font-medium">{label}:</span>
        <span className="col-span-2 font-mono text-sm">{value}</span>
      </div>
    );
  };

  const renderArrayField = (label: string, arr: any[]) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return null;

    return (
      <div className="py-2 border-b border-gray-100">
        <span className="text-gray-600 font-medium block mb-2">
          {label} ({arr.length} items):
        </span>
        <div className="ml-4 space-y-2 max-h-32 overflow-y-auto">
          {arr.map((item, index) => (
            <div key={index} className="text-xs font-mono bg-gray-50 p-2 rounded">
              {typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0 !mb-0">
      <div
        className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden ${
          isProductRecognitionItem(item) || isPogItem(item) || isTemplateItem(item)
            ? 'max-w-5xl'
            : 'max-w-2xl'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isProductRecognitionItem(item)
              ? 'Detection Details'
              : isPogItem(item)
                ? 'POG Product Details'
                : isTemplateItem(item)
                  ? 'Template Product Details'
                  : 'Item Details'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {isPogItem(item) || isTemplateItem(item) ? (
            <PogProductContent
              item={item}
              chainId={chainId}
              storeId={storeId}
              storeName={storeName}
              shouldShowEmbeddings={shouldShowEmbeddings}
            />
          ) : isProductRecognitionItem(item) && cameraImageElement ? (
            <div className="p-6">
              <ProductRecognitionDetectionContent
                item={item}
                cameraImageElement={cameraImageElement}
                layerBboxColor={layerBboxColor}
                chainId={chainId}
                storeId={storeId}
                onUpcSubmit={onUpcSubmit ? handleUpcSubmit : undefined}
                planogramComplianceData={planogramComplianceData}
                cameraImage={cameraImage}
                cartonBoxes={cartonBoxes}
                withReferenceCrops
              />

              {/* Additional Technical Details Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                {inferenceMetadataFiltered && Object.keys(inferenceMetadataFiltered).length > 0 && (
                  <div className="py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium block mb-2">
                      Inference Metadata:
                    </span>
                    <div className="ml-4 space-y-1">
                      {Object.entries(inferenceMetadataFiltered).map(([key, value]) => (
                        <div key={key} className="grid grid-cols-3 gap-4">
                          <span className="text-gray-500 text-sm">{humanizeMetadataKey(key)}:</span>
                          <span className="col-span-2 font-mono text-xs">
                            {formatMetadataValue(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Raw JSON for debugging */}
                <div className="py-2">
                  <span className="text-gray-600 font-medium block mb-2">Raw Data:</span>
                  <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto max-h-200 overflow-y-auto">
                    {JSON.stringify(item, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="space-y-1">
                {/* Basic Properties */}
                {renderDetailField('Type', item.type)}
                {renderDetailField(
                  'Confidence',
                  item.confidence ? `${(item.confidence * 100).toFixed(1)}%` : undefined
                )}

                {/* Coordinates for BBox */}
                {'x1' in item && (
                  <>
                    {renderDetailField('X1', item.x1)}
                    {renderDetailField('Y1', item.y1)}
                    {renderDetailField('X2', item.x2)}
                    {renderDetailField('Y2', item.y2)}
                    {renderDetailField('Width', item.x2 - item.x1)}
                    {renderDetailField('Height', item.y2 - item.y1)}
                  </>
                )}

                {/* Points for Polygon */}
                {'points' in item && renderArrayField('Points', item.points)}

                {/* Shape */}
                {item.shape && renderArrayField('Shape', item.shape)}

                {/* Position Properties */}
                {renderDetailField('Column', item.column)}
                {renderDetailField('Mod', item.mod)}
                {renderDetailField('Shelf', item.shelf)}

                {/* OCR Data */}
                {item.ocr && (
                  <div className="py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium block mb-2">OCR:</span>
                    <div className="ml-4 space-y-1">
                      {item.ocr.text && (
                        <div className="grid grid-cols-3 gap-4">
                          <span className="text-gray-500 text-sm">text:</span>
                          <span className="col-span-2 font-mono text-xs">"{item.ocr.text}"</span>
                        </div>
                      )}
                      {item.ocr.confidence !== undefined && (
                        <div className="grid grid-cols-3 gap-4">
                          <span className="text-gray-500 text-sm">confidence:</span>
                          <span className="col-span-2">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceClass(
                                item.ocr.confidence
                              )}`}
                            >
                              {mapOcrConfidenceToLabel(item.ocr.confidence)}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Product Recognition */}
                {'productName' in item &&
                  renderDetailField('Product Name', (item as any).productName)}
                {'upc' in item && renderDetailField('UPC', (item as any).upc)}
                {'normalizedBarcode' in item &&
                  renderDetailField('Normalized Barcode', (item as any).normalizedBarcode)}
                {'itemNumber' in item && renderDetailField('Item Number', (item as any).itemNumber)}
                {'productId' in item && renderDetailField('Product ID', (item as any).productId)}

                {/* Visual Similarity Candidates */}
                {item.vizSimCandidates && item.vizSimCandidates.length > 0 && (
                  <div className="py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium block mb-2">
                      Visual Similarity Candidates ({item.vizSimCandidates.length}):
                    </span>
                    <div className="ml-4 space-y-3 max-h-40 overflow-y-auto">
                      {item.vizSimCandidates.map((candidate: any, index: number) => (
                        <div key={index} className="bg-gray-50 p-3 rounded text-xs">
                          <div className="font-medium text-gray-900 mb-1">
                            {candidate.productName || 'Unknown Product'}
                          </div>
                          <div className="space-y-1 text-gray-600">
                            <div>Barcode: {candidate.normalizedBarcode}</div>
                            <div>
                              Similarity:{' '}
                              {candidate.similarity !== null
                                ? (candidate.similarity * 100).toFixed(1) + '%'
                                : ''}
                            </div>
                            <div>Confident: {candidate.isConfident ? 'Yes' : 'No'}</div>
                            {candidate.itemNumber && <div>Item: {candidate.itemNumber}</div>}
                            {candidate.productId && <div>ID: {candidate.productId}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {inferenceMetadataFiltered && Object.keys(inferenceMetadataFiltered).length > 0 && (
                  <div className="py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium block mb-2">
                      Inference Metadata:
                    </span>
                    <div className="ml-4 space-y-1">
                      {Object.entries(inferenceMetadataFiltered).map(([key, value]) => (
                        <div key={key} className="grid grid-cols-3 gap-4">
                          <span className="text-gray-500 text-sm">{humanizeMetadataKey(key)}:</span>
                          <span className="col-span-2 font-mono text-xs">
                            {formatMetadataValue(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Raw JSON for debugging */}
                <div className="py-2">
                  <span className="text-gray-600 font-medium block mb-2">Raw Data:</span>
                  <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto max-h-200 overflow-y-auto">
                    {JSON.stringify(item, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
